import { Box, Spinner } from 'grommet';
import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { API } from '../service/common/api';
import { getUserMfaPreference } from '../service/common/mfa.service';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MapPage } from '../pages/map/Map.page';
import jwtDecode from 'jwt-decode';

// lazy loading
const UsersPage = React.lazy(() => import('../pages/admin/Users.page'));
const ApiDocsPage = React.lazy(() => import('../pages/docs/ApiDocs.page'));
const Dashboards = React.lazy(() => import('../pages/dashboards/Dashboards.page'));
const EstimationPage = React.lazy(() => import('../pages/estimation-tool/Estimation.page'));
const CustomerProfile = React.lazy(() => import('../pages/customer-profile/lib/CustomerProfile'));
const Home = React.lazy(() => import('../pages/home/Home.page'));
const Leads = React.lazy(() => import('../pages/lead-list/LeadList.page'));
const ProducerList = React.lazy(() => import('../pages/producer-list/ProducerList.page'));
const MfaPage = React.lazy(() => import('../pages/mfa/Mfa.page'));
const InsertCodeMfa = React.lazy(() => import('../pages/mfa/Mfa.insert.code'));
const ErrorPage = React.lazy(() => import('../pages/mfa/Mfa.error.page'));
const UnderMaintenance = React.lazy(() => import('../components/common/UnderMaintenance'));

const RoutingProvider = (): JSX.Element => {
  const { pathname } = useLocation();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [showMfaPage, setShowMfaPage] = useState(false);
  const accessToken = localStorage.getItem('awsToken');
  const responseMfa = location.state?.response;
  const { showUnderMaintenancePage, auditorAccessControl, dashboardPermissions } = useFlags();

  // When navigating between pages, we expect the scrollbar to go to the top, instead of staying where it was on the old page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const fetchData = async (accessToken) => {
      try {
        const idToken = API.getAccessToken();
        const decodedToken: any = jwtDecode(idToken);
        const mfaIsRequired = decodedToken.mfaRequired;
        const preference = await getUserMfaPreference(accessToken);
        if (mfaIsRequired === 'true' && !preference?.includes('SOFTWARE_TOKEN_MFA') && responseMfa !== 'SUCCESS') {
          setShowMfaPage(true);
          setLoading(false);
        } else {
          setShowMfaPage(false);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking if MFA is required:', error);
      }
    };

    fetchData(accessToken);
  }, [accessToken, responseMfa]);

  const getFallback = (component) => {
    return (
      <Suspense
        fallback={
          <Box direction='row' gap='medium' style={{ margin: '1rem auto' }}>
            <Spinner size='medium' color={'#FFE137'} />
          </Box>
        }>
        {component}
      </Suspense>
    );
  };

  if (loading) {
    return <Spinner pad={'24px'} margin={'24px'} />;
  }

  if (showUnderMaintenancePage) {
    return (
      <Routes>
        <Route path='*' element={getFallback(<UnderMaintenance />)} />
      </Routes>
    );
  }

  const ProtectedRoute = ({
    flag,
    allowed,
    redirectTo,
    children,
  }: {
    flag: boolean;
    allowed: boolean;
    redirectTo: string;
    children: JSX.Element;
  }) => {
    if (flag && !allowed) {
      return <Navigate to={redirectTo} />;
    }
    return children;
  };

  return (
    <Routes>
      <Route path='/' element={showMfaPage ? <Navigate to='/mfa' /> : <Navigate to='/home' />} />
      <Route
        path='/home'
        element={
          <ProtectedRoute flag={auditorAccessControl} allowed={false} redirectTo='/producers'>
            {getFallback(<Home />)}
          </ProtectedRoute>
        }
      />
      <Route path='/mfa' element={getFallback(<MfaPage />)} />
      <Route path='/error' element={getFallback(<ErrorPage />)} />
      <Route path='/code' element={getFallback(<InsertCodeMfa />)} />
      <Route
        path='/leads'
        element={
          <ProtectedRoute flag={auditorAccessControl} allowed={false} redirectTo='/producers'>
            {showMfaPage ? <Navigate to='/mfa' /> : getFallback(<Leads />)}
          </ProtectedRoute>
        }
      />
      <Route path='/producers' element={showMfaPage ? <Navigate to='/mfa' /> : getFallback(<ProducerList />)} />
      <Route path='/admin/users' element={getFallback(<UsersPage />)} />
      <Route path='/admin/api-docs' element={getFallback(<ApiDocsPage />)} />
      <Route path='/dashboards' element={dashboardPermissions === 'none' ? <Navigate to='/home' /> : getFallback(<Dashboards />)} />
      <Route
        path='/estimator'
        element={
          <ProtectedRoute flag={auditorAccessControl} allowed={false} redirectTo='/producers'>
            {showMfaPage ? <Navigate to='/mfa' /> : getFallback(<EstimationPage />)}
          </ProtectedRoute>
        }
      />
      <Route
        path='/estimator/:journeyId'
        element={
          <ProtectedRoute flag={auditorAccessControl} allowed={false} redirectTo='/producers'>
            {showMfaPage ? <Navigate to='/mfa' /> : getFallback(<EstimationPage />)}
          </ProtectedRoute>
        }
      />
      <Route path='/leads/:journeyId' element={showMfaPage ? <Navigate to='/mfa' /> : getFallback(<CustomerProfile />)} />
      <Route
        path='/fields/:journeyId'
        element={
          <ProtectedRoute flag={auditorAccessControl} allowed={false} redirectTo='/producers'>
            {getFallback(<MapPage />)}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};
export default React.memo(RoutingProvider);
